// Portfolio Analysis — full loan-portfolio audit tool (Phase 1: LoanDisk loan families).
import { useEffect, useState } from "react";
import { paStatus, setPaAsOf } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Shield, BarChart3, Layers, Table2, AlertTriangle, RefreshCw, BookOpen, Scale, MapPin } from "lucide-react";
import PaGeography from "./PaGeography";
import PaOverview from "./PaOverview";
import { PaVintages, PaSegments } from "./PaVintagesSegments";
import PaLoans from "./PaLoans";
import { PaSync, PaDataQuality, PaAbout, PaReconcile } from "./PaSyncDqAbout";

const TABS = [
  ["overview", "Overview", BarChart3], ["vintages", "Vintages", Layers], ["segments", "Segments", Layers], ["geography", "Geography", MapPin], ["loans", "Loans", Table2],
  ["dq", "Data quality", AlertTriangle], ["sync", "Sync status", RefreshCw], ["reconcile", "Reconciliation", Scale], ["about", "About this data", BookOpen],
];

function RegionToggle({ region, setRegion }) {
  return (
    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs" data-testid="pa-region-toggle">
      {["UK", "AUS"].map((r) => (
        <button key={r} onClick={() => setRegion(r)} className={`px-3 py-1.5 font-semibold transition-colors ${region === r ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`pa-region-${r}`}>{r === "AUS" ? "Australia" : "United Kingdom"}</button>
      ))}
    </div>
  );
}

export default function PortfolioAnalysis() {
  const [tab, setTab] = useState("overview");
  const [region, setRegion] = useState("UK");
  const [verified, setVerified] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [asOf, setAsOf] = useState("");
  useEffect(() => { paStatus().then((s) => { setVerified(!!s.phase2_verified); setSnaps([...(s.regions?.UK?.snapshots || []), ...(s.regions?.AUS?.snapshots || [])]); }).catch(() => {}); }, []);
  useEffect(() => { setPaAsOf(asOf); return () => setPaAsOf(null); }, [asOf]);
  const regional = !["overview", "sync", "about", "reconcile"].includes(tab);
  const regionSnaps = snaps.filter((s) => s.region === region || tab === "overview");
  return (
    <div className="p-6 space-y-5" data-testid="portfolio-analysis-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}><Shield className="w-6 h-6 text-emerald-600" /> Portfolio Analysis</h1>
          <p className="text-sm text-slate-500 mt-1">One row per loan family across the UK and Australian books — performance, vintages and segments, built read-only from LoanDisk.</p>
        </div>
        <div className="flex items-center gap-2">
          {asOf && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300" data-testid="pa-asof-badge">Viewing snapshot as at {asOf}</Badge>}
          {(regional || tab === "overview") && (
            <select value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white" data-testid="pa-asof-select" title="View the book as it stood at a past month-end">
              <option value="">As at: Live (today)</option>
              {regionSnaps.map((s) => <option key={s.snapshot_id} value={s.as_of}>As at {s.as_of}{s.trigger === "monthly cron" ? " · month-end" : ""} ({s.families} families)</option>)}
            </select>
          )}
          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">Phase 1 · LoanDisk · approved</Badge>
          {verified === false && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300" data-testid="pa-phase2-badge">Phase 2 · director reports · Unverified</Badge>}
          {verified === true && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300" data-testid="pa-phase2-badge">Phase 2 · director reports · approved</Badge>}
          {regional && <RegionToggle region={region} setRegion={setRegion} />}
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab} key={asOf || "live"}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
          {TABS.map(([v, l, Icon]) => <TabsTrigger key={v} value={v} className="text-xs data-[state=active]:bg-white data-[state=active]:text-emerald-700" data-testid={`pa-tab-${v}`}><Icon className="w-3.5 h-3.5 mr-1" />{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview" className="mt-4"><PaOverview /></TabsContent>
        <TabsContent value="vintages" className="mt-4"><PaVintages region={region} /></TabsContent>
        <TabsContent value="geography" className="mt-4"><PaGeography region={region} /></TabsContent>
        <TabsContent value="segments" className="mt-4"><PaSegments region={region} /></TabsContent>
        <TabsContent value="loans" className="mt-4"><PaLoans region={region} /></TabsContent>
        <TabsContent value="dq" className="mt-4"><PaDataQuality region={region} /></TabsContent>
        <TabsContent value="sync" className="mt-4"><PaSync /></TabsContent>
        <TabsContent value="reconcile" className="mt-4"><PaReconcile /></TabsContent>
        <TabsContent value="about" className="mt-4"><PaAbout /></TabsContent>
      </Tabs>
    </div>
  );
}
